import React, { useMemo, useState } from 'react';

import { Box, Button, Popover, TextField } from '@mui/material';
import { styled, useTheme, Theme } from '@mui/material/styles';
import 'react-quill/dist/quill.snow.css';
import * as ReactQuill from 'react-quill';

interface ReactQuillProps extends ReactQuill.default {
  muiTheme: Theme;
  error?: boolean;
}

const QuillEditorRoot = styled(ReactQuill.default)<ReactQuillProps>(({ muiTheme: theme, readOnly, error }) => {
  return {
    border: readOnly ? 0 : 1,
    borderColor: error ? theme.palette.error.main : theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
    borderStyle: 'solid',
    display: 'flex',
    flexDirection: 'column',
    '& .ql-snow.ql-toolbar': readOnly
      ? { display: 'none' }
      : {
          backgroundColor: 'rgba(255, 255, 255, 0.09)',
          borderColor: theme.palette.divider,
          borderRadius: '8px 8px 0 0',
          borderLeft: 'none',
          borderRight: 'none',
          borderTop: 'none',
          '& .ql-picker-label:hover': {
            color: theme.palette.primary.main,
            '& .ql-stroke': {
              stroke: theme.palette.primary.main,
            },
          },
          '& .ql-picker-label.ql-active': {
            color: theme.palette.primary.main,
            '& .ql-stroke': {
              stroke: theme.palette.primary.main,
            },
            '& .ql-fill': {
              fill: theme.palette.primary.main,
            },
          },
          '& .ql-picker-item:hover': {
            color: theme.palette.primary.main,
          },
          '& .ql-picker-item.ql-selected': {
            color: theme.palette.primary.main,
          },
          '& button:hover': {
            color: theme.palette.primary.main,
            fill: theme.palette.primary.main,

            '& .ql-fill': {
              fill: theme.palette.primary.main,
            },

            '& .ql-stroke': {
              stroke: theme.palette.primary.main,
            },
          },
          '& button:focus': {
            color: theme.palette.primary.main,

            '& .ql-fill': {
              fill: theme.palette.primary.main,
            },

            '& .ql-stroke': {
              stroke: theme.palette.primary.main,
            },
          },
          '& button.ql-active': {
            '& .ql-fill': {
              fill: theme.palette.primary.main,
            },

            '& .ql-stroke': {
              stroke: theme.palette.primary.main,
            },
          },

          '& .ql-stroke': {
            stroke: theme.palette.text.primary,
          },

          '& .ql-fill': {
            fill: theme.palette.text.primary,
          },

          '& .ql-picker': {
            color: theme.palette.text.primary,
          },
          '& .ql-picker-options': {
            backgroundColor: theme.palette.background.paper,
            border: 'none',
            borderRadius: theme.shape.borderRadius,
            boxShadow: theme.shadows[10],
            padding: theme.spacing(2),
          },
        },
    '& .ql-snow.ql-container': {
      backgroundColor: theme.palette.background.default,
      borderBottom: 'none',
      borderColor: error ? theme.palette.error.main : theme.palette.divider,
      borderLeft: 'none',
      borderRight: 'none',
      flexGrow: 1,
      overflow: 'auto',
      borderRadius: '0 0 8px 8px',
      '& .ql-editor': {
        height: '100%',
        color: readOnly ? theme.palette.text.secondary : theme.palette.text.primary,
        fontFamily: theme.typography.body1.fontFamily,
        fontSize: theme.typography.body1.fontSize,
        padding: readOnly ? 0 : theme.spacing(2),
        '&.ql-blank::before': {
          color: theme.palette.text.secondary,
          fontStyle: 'normal',
          left: theme.spacing(2),
        },
      },
    },
  };
});

interface QuillElementObj {
  container: Element;
  controls: { [key: string]: unknown };
  handlers: { [key: string]: unknown };
  options: { [key: string]: unknown };
  quill: ReactQuill.Quill;
}

export const QuillEditor = ({ ...props }) => {
  const theme = useTheme();

  const [quillElement, setQuillElement] = useState<QuillElementObj | null>(null);

  const handleClose = () => {
    setQuillElement(null);
    setImageUrl('');
  };

  const open = Boolean(quillElement?.container);
  const id = open ? 'simple-popover' : undefined;

  function handleImage(this: QuillElementObj) {
    setQuillElement(this);
  }

  const Editor = useMemo(() => {
    return {
      modules: {
        toolbar: {
          container: [
            [{ header: '1' }, { header: '2' }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
            ['link', 'image'],
            ['clean'],
          ],
          handlers: {
            image: handleImage,
          },
        },
        clipboard: {
          matchVisual: false,
        },
      },
      formats: [
        'header',
        'bold',
        'italic',
        'underline',
        'strike',
        'blockquote',
        'list',
        'bullet',
        'indent',
        'link',
        'image',
      ],
    };
  }, []);

  const [imageUrl, setImageUrl] = useState('');

  const handleImageUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setImageUrl(event.target.value);
  };

  const handleImageSave = () => {
    console.log(quillElement);

    const range = quillElement?.quill.getSelection();
    const position = range ? range.index : 0;

    if (imageUrl) {
      quillElement?.quill.insertEmbed(position, 'image', imageUrl);
      handleClose();
    }
  };

  return (
    <>
      {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        /* @ts-ignore */
        <QuillEditorRoot
          aria-describedby={id}
          muiTheme={theme}
          modules={Editor.modules}
          formats={Editor.formats}
          {...props}
        />
      }
      <Popover
        id={id}
        open={open}
        anchorEl={quillElement?.container}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{ sx: { mt: 2 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-end', m: 2, mt: 1 }}>
          <TextField
            id="standard-basic"
            label="Image Link"
            variant="standard"
            value={imageUrl}
            onChange={handleImageUrlChange}
            sx={{ mr: 2 }}
            size="small"
          />
          <Button onClick={handleImageSave} variant="contained" size="small">
            Save
          </Button>
        </Box>
      </Popover>
    </>
  );
};
